/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Predictive traits", description = "Predictive traits use machine learning to forecast user behavior, giving you forward-looking signals like purchase propensity or churn risk.", lastUpdatedDate = new Date(1788480000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Predictive traits",
        description: "Predictive traits use machine learning to forecast user behavior, giving you forward-looking signals like purchase propensity or churn risk.",
        lastUpdatedDate: "Fri Sep 04 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Configure predictive inputs",
          "depth": 0,
          "id": "configure-predictive-inputs"
        }, {
          "value": "Create a predictive trait",
          "depth": 0,
          "id": "create-a-predictive-trait"
        }, {
          "value": "View and use predictive traits",
          "depth": 0,
          "id": "view-and-use-predictive-traits"
        }, {
          "value": "Analyze predictions",
          "depth": 0,
          "id": "analyze-predictions"
        }, {
          "value": "Cumulative gains",
          "depth": 1,
          "id": "cumulative-gains"
        }, {
          "value": "Precision vs. random",
          "depth": 1,
          "id": "precision-vs-random"
        }, {
          "value": "Predicted vs. actual conversion rate",
          "depth": 1,
          "id": "predicted-vs-actual-conversion-rate"
        }, {
          "value": "Top feature importance",
          "depth": 1,
          "id": "top-feature-importance"
        }, {
          "value": "Use predictive traits in audiences",
          "depth": 0,
          "id": "use-predictive-traits-in-audiences"
        }, {
          "value": "Sync predictive audiences",
          "depth": 0,
          "id": "sync-predictive-audiences"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      code: "code",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and analytics/data engineers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A defined ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "schema"
                  }), " with at least one parent model (for example, ", _jsx(_components.code, {
                    children: "Users"
                  }), ")."]
                }), _jsx("li", {
                  children: "Event data that reflects the outcome you want to predict (for example, purchases, cart completions)."
                }), _jsx("li", {
                  children: "(Optional) Connected destinations for syncing audiences."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Predictive traits use machine learning to score how likely each user is to take a future action, so you can target people by what they're about to do, not just what they've already done."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "When defining audiences, you sometimes want to act on signals about what customers are likely to do next, not just what they have already done."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Predictive traits"
        }), " extend Customer Studio traits with AI-powered scores that estimate the likelihood of a future action — or the value that action will produce."]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Purchase propensity:"
          }), " probability that a user will make a purchase within the next 7 days"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cart completion propensity:"
          }), " probability that a user who started checkout will complete their purchase"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Churn prediction:"
          }), " probability that a user will stop performing a key event (such as a purchase or login) within a given timeframe"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Predicted value:"
          }), " the revenue a user is expected to generate within a given timeframe"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Predictive traits are refreshed automatically on a schedule so your campaigns always use the latest predictions."
      }), "\n", _jsxs(_components.p, {
        children: ["The predictive traits suite also includes ", _jsx(_components.a, {
          href: "/customer-studio/catalog-recommendations",
          children: "catalog recommendations"
        }), " for personalized item-level recommendations and ", _jsx(_components.a, {
          href: "/customer-studio/lookalike-audiences",
          children: "lookalike audiences"
        }), " for finding users similar to your best segments."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Predictive traits are built on the same machine learning models used in\nHightouch ", _jsx(_components.a, {
            href: "/ai-decisioning/overview",
            children: _jsx(_components.strong, {
              children: "AI Decisioning"
            })
          }), ". They are\nautomatically trained on your historical event data and do not require custom\nML expertise."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "configure-predictive-inputs",
        children: "Configure predictive inputs"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you create a predictive trait, you must define ", _jsx(_components.strong, {
          children: "predictive inputs"
        }), " in your schema. Predictive inputs are the user properties and event data that models use for training and scoring."]
      }), "\n", _jsx(_components.p, {
        children: "To configure predictive inputs:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Schema"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select your parent model (for example, ", _jsx(_components.code, {
            children: "Users"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Predictive Inputs"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "User Properties"
          }), ", select the columns from this model that can help identify patterns — for example, ", _jsx(_components.code, {
            children: "age"
          }), ", ", _jsx(_components.code, {
            children: "country"
          }), ", ", _jsx(_components.code, {
            children: "device"
          }), ", ", _jsx(_components.code, {
            children: "plan_tier"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Key events"
          }), ", add the event models that are relevant to the prediction target. Events must have a primary key configured to be available for selection."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/predictive-inputs-tab.png",
          alt: "Predictive Inputs tab on a parent model showing user properties and key events configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "Once saved, these inputs are reusable across multiple predictive traits, catalog recommendations, and lookalike audiences."
      }), "\n", _jsx(_components.h2, {
        id: "create-a-predictive-trait",
        children: "Create a predictive trait"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Traits"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create > New trait"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Method"
          }), " step, select the ", _jsx(_components.strong, {
            children: "Model"
          }), " you want to score (your parent model, for example ", _jsx(_components.code, {
            children: "Users"
          }), "), then choose ", _jsx(_components.strong, {
            children: "Prediction"
          }), " as the calculation method. Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/pt-new-trait.png",
          alt: "Method step showing the model dropdown and the Prediction calculation method"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Calculation"
          }), " step, under ", _jsx(_components.strong, {
            children: "What action do you want to predict?"
          }), ", select the event to forecast (for example, ", _jsx(_components.code, {
            children: "Purchases"
          }), "). Only events configured as predictive inputs, with a primary key, appear here."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/prediction-select-event.png",
          alt: "Event selection showing \"What action do you want to predict?\" with an event selected"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "What kind of prediction is this?"
            }), ", choose the type of score. The timeframe control below adapts to the type you pick."]
          }), "\n", _jsxs(_components.table, {
            children: [_jsx(_components.thead, {
              children: _jsxs(_components.tr, {
                children: [_jsx(_components.th, {
                  children: "Prediction type"
                }), _jsx(_components.th, {
                  children: "What it scores"
                }), _jsx(_components.th, {
                  children: "Timeframe"
                })]
              })
            }), _jsxs(_components.tbody, {
              children: [_jsxs(_components.tr, {
                children: [_jsx(_components.td, {
                  children: _jsx(_components.strong, {
                    children: "Likelihood of event"
                  })
                }), _jsx(_components.td, {
                  children: "How likely each user is to perform the event"
                }), _jsx(_components.td, {
                  children: "Event performed within the next N days"
                })]
              }), _jsxs(_components.tr, {
                children: [_jsx(_components.td, {
                  children: _jsx(_components.strong, {
                    children: "Likelihood of churn"
                  })
                }), _jsx(_components.td, {
                  children: "How likely each user is to stop performing the event"
                }), _jsx(_components.td, {
                  children: "Churned after no events for N days"
                })]
              }), _jsxs(_components.tr, {
                children: [_jsx(_components.td, {
                  children: _jsx(_components.strong, {
                    children: "Value of event"
                  })
                }), _jsx(_components.td, {
                  children: "A numeric value from the event, such as total revenue"
                }), _jsx(_components.td, {
                  children: "Value predicted over the next N days"
                })]
              })]
            })]
          }), "\n", _jsxs(_components.p, {
            children: ["For ", _jsx(_components.strong, {
              children: "Value of event"
            }), ", also choose the aggregation and numeric property to predict — for example, the sum of ", _jsx(_components.code, {
              children: "PURCHASE_VALUE"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/predictive-traits/pt-new-calculation.png",
              alt: "Calculation step showing the three prediction types, the event selector, the timeframe, and the update schedule"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["(Optional) Under ", _jsx(_components.strong, {
              children: "Who should this prediction apply to?"
            }), ", add filters to score only certain users — for example, people who have already signed up or started checkout."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "How often should scores be updated?"
            }), ", set the refresh cadence, then click ", _jsx(_components.strong, {
              children: "Continue"
            }), ". More frequent updates keep scores fresh but use more warehouse resources."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In the ", _jsx(_components.strong, {
              children: "Finalize"
            }), " step, name the trait, optionally add a description, review the calculation summary, and click ", _jsx(_components.strong, {
              children: "Create trait"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/pt-trait-finalize.png",
          alt: "Finalize step showing the trait name, description, training notice, and calculation summary"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch trains a model on your data and generates a score for each user. Training runs in three phases — prepare data, train model, run inference — and typically takes several hours, depending on warehouse size and data volume."
      }), "\n", _jsx(_components.h2, {
        id: "view-and-use-predictive-traits",
        children: "View and use predictive traits"
      }), "\n", _jsxs(_components.p, {
        children: ["After training completes, predictive traits appear in the Traits list with type ", _jsx(_components.strong, {
          children: "Prediction"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Preview results"
          }), " in the trait details page"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use in audiences"
          }), " by filtering on score thresholds or percentile ranges"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync predictions"
          }), " to destinations such as ad platforms, ESPs, or CRMs for activation"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "analyze-predictions",
        children: "Analyze predictions"
      }), "\n", _jsxs(_components.p, {
        children: ["After training finishes, open the ", _jsx(_components.strong, {
          children: "Prediction analysis"
        }), " tab on the trait to see how well the model performs. It reports the number of users scored and the last run date, and every metric is measured on a held-out sample the model never saw during training."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/prediction-analysis-tab.png",
          alt: "Prediction analysis tab showing cumulative gains, precision vs. random, predicted vs. actual conversion rate, and top feature importance"
        })
      }), "\n", _jsx(_components.h3, {
        id: "cumulative-gains",
        children: "Cumulative gains"
      }), "\n", _jsx(_components.p, {
        children: "The cumulative gains curve ranks every user by how likely they are to convert, then plots the share of all converters you reach as you target more of that ranked list. The steeper the curve climbs above the diagonal — which represents random targeting — the more converters you reach by targeting a small, high-scoring slice of the audience."
      }), "\n", _jsx(_components.h3, {
        id: "precision-vs-random",
        children: "Precision vs. random"
      }), "\n", _jsxs(_components.p, {
        children: ["This compares how concentrated the model's high scores are among users who actually converted against random ranking, and reports the ", _jsx(_components.strong, {
          children: "lift"
        }), " — how many times better than random the model is at pushing real converters toward the top. Higher lift means a sharper model."]
      }), "\n", _jsx(_components.h3, {
        id: "predicted-vs-actual-conversion-rate",
        children: "Predicted vs. actual conversion rate"
      }), "\n", _jsx(_components.p, {
        children: "Each pair of bars represents one tenth of the audience, ordered by model score. Comparing the predicted conversion rate against the rate that actually happened shows whether the scores are well calibrated: the two bars in each decile should sit close together."
      }), "\n", _jsx(_components.h3, {
        id: "top-feature-importance",
        children: "Top feature importance"
      }), "\n", _jsx(_components.p, {
        children: "This ranks the user properties and events that contribute most to the model's scores, each as a share of total importance. Use it to confirm the model is learning from signals you'd expect."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Use percentile ranges to build high-value segments. For example, create an\naudience of \"Top 20% purchase propensity\" users and sync them to ad channels\nfor efficient spend."
        })
      }), "\n", _jsx(_components.h2, {
        id: "use-predictive-traits-in-audiences",
        children: "Use predictive traits in audiences"
      }), "\n", _jsx(_components.p, {
        children: "You can use predictive traits when defining audiences the same way you use other traits."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Audiences"
          }), " and click ", _jsx(_components.strong, {
            children: "Add audience"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "In the audience definition, select a trait."
        }), "\n", _jsx(_components.li, {
          children: "Set the score range or percentile range you want to include (for example, 80–100%)."
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) Combine the predictive filter with other attributes, such as ", _jsx(_components.code, {
            children: "country = US"
          }), " or ", _jsx(_components.code, {
            children: "plan_type = paid"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/pt-audience-filters.png",
          alt: "Audience with predictive trait filter"
        })
      }), "\n", _jsx(_components.p, {
        children: "Predictive traits can be layered with demographic or behavioral conditions, giving you highly targeted segments. For example:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "High propensity to purchase + specific geography"
          }), " (US, Canada)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cart completion propensity + recent site activity"
          }), " (visited in the last 7 days)"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Predictive traits update on a schedule. Between updates, some people may\ncomplete the action you're predicting (like making a purchase), but their\nscore won't change until the next update. To keep your audience focused on\npeople who haven't yet acted, ", _jsx(_components.strong, {
            children: "add a filter that excludes people who have\nalready performed the predicted event"
          }), " (for example, ", _jsx(_components.code, {
            children: "Purchases = 0"
          }), " or\n", _jsx(_components.code, {
            children: "Purchases (in last 7 days) = 0"
          }), "). Your campaign then includes only users who\nare still likely to take action."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-predictive-audiences",
        children: "Sync predictive audiences"
      }), "\n", _jsx(_components.p, {
        children: "Once you define an audience with predictive traits, you can sync it to any connected destination the same way you sync other audiences."
      }), "\n", _jsx(_components.p, {
        children: "For example, you might sync:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "High purchase propensity"
          }), " users to Google Ads or Meta Ads for paid acquisition"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "High cart completion propensity"
          }), " users to an ESP for checkout reminder emails"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When syncing, predictive scores are included alongside user attributes so downstream tools can use them directly."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/pt-sync-view.png",
          alt: "Sync configuration with predictive scores"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After syncs run, you can monitor performance and health in the ", _jsx(_components.strong, {
          children: "Syncs > Overview"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/predictive-traits/pt-sync-overview.png",
          alt: "Sync overview with predictive audience"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You don't need to configure anything special for predictive traits when\nsyncing. They behave like any other trait and can be mapped directly to\ndestination fields."
        })
      }), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsxs(_components.p, {
        children: ["If a predictive trait run fails, Hightouch assigns an error code that identifies the cause. The table below covers all predictive trait error codes. For a complete reference of all Hightouch error codes, see ", _jsx(_components.a, {
          href: "/developer-tools/error-codes#predictive-traits-errors",
          children: "Error codes"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_USERS_IN_INPUT_FILE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The training audience returned no users."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that the parent audience or model query returns rows before training starts."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_USER_ROWS_AFTER_FILTERS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Users were present initially, but predictive filters removed all of them."
            }), _jsx(_components.td, {
              align: "left",
              children: "Loosen the filters or verify that event and property conditions match real data."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_TRAINING_DATA_FILES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't generate the training dataset."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm that the configured events return data and the event model is queryable."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LABEL_FREQUENCY_TOO_LOW"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "There aren't enough examples of the target outcome to train the model reliably."
            }), _jsx(_components.td, {
              align: "left",
              children: "Increase the lookback window, reduce filters, or choose a higher-volume outcome event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_POSITIVE_EXAMPLES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The training data contains zero positive examples of the target event."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that the outcome event is occurring for users in scope."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TRAINING_MATRIX_DOWNLOAD_FAILED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An internal failure occurred while retrieving the training artifact."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the run. If it persists, ", _jsx(PylonLink, {
                children: "contact support"
              }), " — this is typically not customer-fixable."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_KEY_EVENTS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "No feature-generation events are configured."
            }), _jsx(_components.td, {
              align: "left",
              children: "Add at least one event input in the predictive trait configuration before retraining."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "EVENT_MODEL_MISCONFIGURED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The event model configuration is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the event model exists, has a valid primary key, and includes all referenced columns."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "EVENT_QUERY_FAILED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't query event data from the warehouse."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check warehouse permissions, model SQL, and whether the referenced tables still exist."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SOURCE_ACCOUNT_LOCKED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The warehouse user account is locked."
            }), _jsx(_components.td, {
              align: "left",
              children: "Wait for the lockout to clear, or have your warehouse admin re-enable the account."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "UNKNOWN_EVENT_IN_FILTER"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A predictive filter references an event Hightouch can't resolve."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check for renamed, deleted, or misconfigured events in the predictive trait setup."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "INVALID_EVENT_PROPERTY_CONDITION"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An event-property filter is malformed."
            }), _jsx(_components.td, {
              align: "left",
              children: "Verify the filter syntax and confirm you're using valid string property names."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_EVENT_DATA_FILES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "No event data was produced for training or inference."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the configured events have recent data and event pulls are succeeding."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TRAINING_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An internal training failure occurred."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry once. If it repeats, ", _jsx(PylonLink, {
                children: "contact support"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "CACHED_EVENT_PULL_DISABLED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A required event pull is disabled."
            }), _jsx(_components.td, {
              align: "left",
              children: "Re-enable the event pull in your schema configuration and rerun the predictor."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "MALFORMED_EVENT_DATA"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Numeric event fields contain invalid values."
            }), _jsx(_components.td, {
              align: "left",
              children: "Clean the source data so numeric columns contain only valid numbers (no strings, nulls in numeric-only fields, etc.)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "MODEL_QUERY_FAILED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't query the parent model data needed for the run."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that the parent model still exists and all referenced columns are present."
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/catalog-recommendations",
            children: "Catalog recommendations"
          }), " to rank the top items from a catalog for each user."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/lookalike-audiences",
            children: "Lookalike audiences"
          }), " to find users who resemble a high-value segment without defining an outcome event."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/traits",
            children: "Traits"
          }), " to see how predictive traits sit alongside the other trait types."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to filter on a predictive score in the visual builder."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/experiments",
            children: "Experiments"
          }), " to measure the lift a predictive audience actually drives."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
